import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react'
import { fetchMemberships } from '@/lib/auth'
import type { OrganizationMembership } from '@/types/database'
import { useAuth } from './AuthContext'

const STORAGE_KEY = 'camp.currentOrgId'

interface OrganizationContextValue {
  memberships: OrganizationMembership[]
  currentOrganizationId: string | null
  currentMembership: OrganizationMembership | null
  isLoading: boolean
  switchOrganization: (organizationId: string) => void
  refresh: () => Promise<void>
}

export const OrganizationContext = createContext<OrganizationContextValue | null>(null)

const fallbackContext: OrganizationContextValue = {
  memberships: [],
  currentOrganizationId: null,
  currentMembership: null,
  isLoading: false,
  switchOrganization: () => {},
  refresh: async () => {},
}

/**
 * Hook returns the active tenant context. Falls back to an empty context
 * when no provider is mounted (e.g. in unit tests rendering a leaf
 * component without the full provider tree).
 */
export function useOrganization(): OrganizationContextValue {
  return useContext(OrganizationContext) ?? fallbackContext
}

function readStoredOrgId(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

function writeStoredOrgId(id: string | null): void {
  try {
    if (id) {
      localStorage.setItem(STORAGE_KEY, id)
    } else {
      localStorage.removeItem(STORAGE_KEY)
    }
  } catch {
    /* ignore storage failures */
  }
}

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth()
  const [memberships, setMemberships] = useState<OrganizationMembership[]>([])
  const [currentOrganizationId, setCurrentOrganizationId] = useState<string | null>(readStoredOrgId)
  const [isLoading, setIsLoading] = useState(false)

  const refresh = useCallback(async () => {
    setIsLoading(true)
    try {
      const result = await fetchMemberships()
      setMemberships(result)

      // If stored org is no longer accessible, fall back to default/first
      const stored = readStoredOrgId()
      const validStored = stored && result.some((m) => m.organization_id === stored)
      if (!validStored) {
        const fallback = result.find((m) => m.is_default) ?? result[0]
        const next = fallback?.organization_id ?? null
        setCurrentOrganizationId(next)
        writeStoredOrgId(next)
      } else {
        setCurrentOrganizationId(stored)
      }
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!isAuthenticated) {
      setMemberships([])
      setCurrentOrganizationId(null)
      writeStoredOrgId(null)
      return
    }
    void refresh()
  }, [isAuthenticated, refresh])

  const switchOrganization = useCallback((organizationId: string) => {
    setCurrentOrganizationId(organizationId)
    writeStoredOrgId(organizationId)
  }, [])

  const currentMembership =
    memberships.find((m) => m.organization_id === currentOrganizationId) ?? null

  return (
    <OrganizationContext.Provider
      value={{
        memberships,
        currentOrganizationId,
        currentMembership,
        isLoading,
        switchOrganization,
        refresh,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  )
}
